import React from 'react'
import qs from 'qs'
import { useHistory, useLocation } from 'react-router'
import { useAppDispatch, useAppSelector } from '../../../hooks/redux'
import { useDrop } from 'react-dnd'

import NoSelect from './NoSelect'
import Header from './Header'
import Alerts from './Alerts'
import TimelineV2 from './TimelineV2'
import ChatBox from './ChatBox'
import TimelineSkeleton from './TimelineV2/Skeleton'
import EmailDrop from '@SonarCX/components/molecules/EmailHolder/EmailDrop'

import { EMAIL_DRAG_TYPE } from '@SonarCX/components/molecules/EmailHolder/Email'
import {
  selectChatBox,
  clearUniqueAlerts,
  updateValue,
  makePrivate
} from '../../../slices/chatBoxSlice'
import { selectPresence } from '../../../slices/presence'
import { selectExtensionAPI } from '@SonarCX/slices/extensionAPI'
import { useQueueState } from '../../../providers/QueueStateProvider'
import { useChatScroll } from '../../../providers/ChatScrollProvider'
import { useAgent } from '../../../providers/AgentProvider'
import { useUserAPI } from '../../../providers/UserAPIProvider'
import { useSocket } from '../../../providers/SocketProvider'
import { useTyping } from '../../../providers/TypingProvider'
import { useTaskAPI } from '../../../providers/TaskAPIProvider'
import useStyles from './styles'

const ChatWindowV2 = ({
  endSession,
  isDirect,
  onlyShowDateSeparators = false
}) => {
  const history = useHistory()
  const location = useLocation()
  const dispatch = useAppDispatch()

  const { height: chatboxHeight } = useAppSelector(selectChatBox)
  const { presence } = useAppSelector(selectPresence)
  const { emails } = useAppSelector(selectExtensionAPI)

  const { tasks } = useTaskAPI()
  const { conversation, id: convoID } = useQueueState()
  const { allUsers } = useUserAPI()
  const { getTyping } = useTyping()
  const { currentUser } = useAgent()
  const { quickScrollToBottom, targetRef, messageEndRef } = useChatScroll()
  const {
    api: { markSeen }
  } = useSocket()

  const [targetID, setTargetID] = React.useState(null)
  const [names, setNames] = React.useState([])
  const [loaded, setLoaded] = React.useState([])
  const [didInitialScroll, setDidInitialScroll] = React.useState(false)
  const messageAreaRef = React.useRef()
  const chatContentAreaRef = React.useRef(null)

  const { isNew } = React.useMemo(
    () => conversation ?? { isNew: false },
    [conversation]
  )
  const typingNames = React.useMemo(
    () => getTyping(conversation?.session),
    [getTyping, conversation]
  )

  const classes = useStyles({ chatboxHeight })

  const handleEmailDrop = React.useCallback(
    ({ id }) => {
      const email = emails[id]
      dispatch(makePrivate())
      dispatch(
        updateValue('file', old => [...(old ?? []), email.file].filter(Boolean))
      )
    },
    [emails, dispatch]
  )

  const [{ isOver, isDragging }, dropRef] = useDrop({
    accept: EMAIL_DRAG_TYPE,
    drop: handleEmailDrop,
    collect: monitor => ({
      isOver: !!monitor.isOver(),
      isDragging: monitor.getItemType() === EMAIL_DRAG_TYPE
    })
  })

  // Resets scroll flag when changing sessions or on new messages
  React.useEffect(() => {
    if (isNew) {
      setDidInitialScroll(false)
      markSeen(convoID, false)
    }
  }, [convoID, isNew, markSeen])

  // Clear offline message
  React.useEffect(() => {
    dispatch(clearUniqueAlerts('offline'))
  }, [convoID, dispatch])

  // Stop scrolling to target
  React.useEffect(() => {
    let mounted = true

    const onScroll = () => {
      mounted && setTargetID(null)
      targetRef.current = null
    }

    const remove = () => document.removeEventListener('wheel', onScroll)

    document.addEventListener('wheel', onScroll, { passive: true })

    return () => {
      mounted = false
      remove()
    }
  }, [targetRef])

  // Handles flow of notification
  React.useEffect(() => {
    if (!location || !history) {
      return
    }

    const params = qs.parse(location.search, { ignoreQueryPrefix: true })
    if (params.id) {
      // Triggers re-render and eventually finds the message to focus on
      setTargetID(params.id)

      // Stop this from happening again
      const newParams = { ...params }
      delete newParams.id
      const newQuery =
        Object.keys(newParams).length === 0
          ? ''
          : qs.stringify(newParams, {
              encode: false,
              addQueryPrefix: true
            })
      history.replace(`${location.pathname}${newQuery}`)
    }
  }, [location, history])

  // After rendering scroll to the latest message or a notification
  React.useEffect(() => {
    quickScrollToBottom()
  }, [convoID, quickScrollToBottom])

  // Waits for tasks to be loaded before showing this timeline
  React.useEffect(() => {
    if (Object.values(tasks).every(task => Object.keys(task).length > 2)) {
      setLoaded(old => [...new Set([...old, conversation?.session])])
    }
  }, [tasks, conversation])

  // Scroll if this timeline is loaded
  React.useEffect(() => {
    if (didInitialScroll) {
      return
    }

    setDidInitialScroll(true)
    quickScrollToBottom()
  }, [conversation, quickScrollToBottom, didInitialScroll])

  // Scroll if is typing is loaded
  React.useEffect(() => {
    if (typingNames.length > 0) {
      quickScrollToBottom()
    }
  }, [typingNames, quickScrollToBottom])

  React.useEffect(() => {
    if (!conversation) {
      return
    }

    if (isDirect) {
      //TODO
      // const users = (conversation.users ?? []).filter(
      //   id => id !== currentUser()?.id
      // )
      // let offline = []
      // users.forEach(id => {
      //   if (presence[id]) {
      //     const p = presence[id]
      //     if (p === 'offline') {
      //       offline.push(id)
      //     }
      //   } else {
      //     offline.push(id)
      //   }
      // })
      // // get names after checking presence
      // let names = []
      // // get names here
      // offline.forEach(id => {
      //   if (allUsers[id]) {
      //     names.push(allUsers[id]?.name)
      //   }
      // })
      // // passing in usernames to msg
      // if (names.length) {
      //   setNames(names)
      // } else {
      //   // resetting banner
      //   setNames('')
      // }
    } else {
      let offline = []
      const id = conversation?.owner_id
      if (presence[id]) {
        const p = presence[id]
        if (p === 'offline') {
          offline.push(id)
        }
      } else {
        offline.push(id)
      }

      let names = []
      // get names here
      offline.forEach(id => {
        if (allUsers[id]) {
          names.push(allUsers[id]?.name)
        }
      })

      // passing in usernames to msg
      if (names.length) {
        setNames(names)
      } else {
        // resetting banner
        setNames([])
      }
    }
  }, [conversation, isDirect, currentUser, presence, allUsers])

  if (!conversation) {
    return <NoSelect />
  }

  return (
    <div className={classes.root}>
      <Header conversation={conversation} endSession={endSession} />

      <div
        className={classes.contentArea}
        ref={el => {
          chatContentAreaRef.current = el
          dropRef(el)
        }}
      >
        <Alerts />

        {(isOver || isDragging) && (
          <EmailDrop center isOver={isOver} text='conversation' />
        )}

        <div className={classes.chatLine}></div>

        <div ref={messageAreaRef} className={classes.chat} id='chat'>
          {conversation?.fetched && loaded.includes(conversation?.session) ? (
            <React.Fragment>
              <TimelineV2
                session={conversation?.session}
                targetID={targetID}
                targetRef={targetRef}
                isDirect={isDirect}
                isClosed={conversation?.closed}
                containerRef={messageAreaRef}
                names={names}
                typingNames={typingNames}
                onlyShowDateSeparators={onlyShowDateSeparators}
                // filterNotes={filter === 'notes'}
              />

              <div ref={messageEndRef} />
            </React.Fragment>
          ) : (
            <TimelineSkeleton />
          )}
        </div>
      </div>

      <ChatBox />
    </div>
  )
}

export default ChatWindowV2
